import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { IRootState } from '@/store';
import { markRead as markReadAction, markAllRead, clearAll } from '@/store/notificationsSlice';
import type { Notification } from '@/store/notificationsSlice';
import { formatIsoDatesInTextBR } from '@/lib/date';

function relativeTime(isoString: string): string {
    const diff = Date.now() - new Date(isoString).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return 'agora mesmo';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}min atrás`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h atrás`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d atrás`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} mes${months > 1 ? 'es' : ''} atrás`;
    const years = Math.floor(months / 12);
    return `${years} ano${years > 1 ? 's' : ''} atrás`;
}

interface NotificationsDrawerProps {
    open: boolean;
    onClose: () => void;
}

export const NotificationsDrawer = ({ open, onClose }: NotificationsDrawerProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const list = useSelector((s: IRootState) => s.notifications.list);
    const unreadCount = useSelector((s: IRootState) => s.notifications.unreadCount);

    const handleClick = async (n: Notification) => {
        if (!n.readAt) {
            try {
                await api.patch(`/api/notifications/${n.id}/read`);
                dispatch(markReadAction(n.id));
            } catch {
                /* noop */
            }
        }
        onClose();
        if (n.url && n.url !== '/') navigate(n.url);
    };

    const handleMarkAllRead = async () => {
        try {
            await api.patch('/api/notifications/read-all');
            dispatch(markAllRead());
        } catch {
            /* noop */
        }
    };

    const handleClearAll = async () => {
        try {
            await api.delete('/api/notifications');
            dispatch(clearAll());
        } catch {
            /* noop */
        }
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-[black]/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-300"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-200"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                    <div className="flex h-full flex-col bg-white dark:bg-surface-card shadow-xl">
                                        {/* Header */}
                                        <div className="flex items-center justify-between px-5 py-4 border-b border-white-light dark:border-surface-border">
                                            <Dialog.Title className="flex items-center font-semibold text-base text-dark dark:text-white">
                                                Notificações
                                                {unreadCount > 0 && (
                                                    <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-danger text-white">
                                                        {unreadCount}
                                                    </span>
                                                )}
                                            </Dialog.Title>
                                            <div className="flex items-center gap-3">
                                                {unreadCount > 0 && (
                                                    <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline font-semibold">
                                                        Marcar lidas
                                                    </button>
                                                )}
                                                {list.length > 0 && (
                                                    <button onClick={handleClearAll} className="text-xs text-danger hover:underline font-semibold">
                                                        Limpar
                                                    </button>
                                                )}
                                                <button
                                                    onClick={onClose}
                                                    className="flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-surface-hover"
                                                    aria-label="Fechar"
                                                >
                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Scrollable list */}
                                        <div className="flex-1 overflow-y-auto">
                                            {list.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-white-dark/40 gap-2">
                                                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path opacity="0.3" d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z" fill="currentColor" />
                                                        <path
                                                            opacity="0.3"
                                                            d="M21 19L20 18V11C20 7.2 17.4 4.1 14 3.2V2C14 0.9 13.1 0 12 0C10.9 0 10 0.9 10 2V3.2C6.6 4.1 4 7.2 4 11V18L3 19C2.4 19.6 2.8 20.7 3.7 20.7H20.4C21.2 20.7 21.6 19.6 21 19Z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                    <p className="text-sm">Nenhuma notificação</p>
                                                </div>
                                            ) : (
                                                list.map((n) => (
                                                    <button
                                                        key={n.id}
                                                        onClick={() => handleClick(n)}
                                                        className={`w-full text-left px-5 py-3.5 border-b border-white-light dark:border-surface-border last:border-b-0 hover:bg-gray-50 dark:hover:bg-surface-hover transition-colors ${
                                                            !n.readAt ? 'bg-primary/5 dark:bg-primary/10' : ''
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${!n.readAt ? 'bg-primary' : 'bg-transparent'}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className={`text-sm font-semibold ${!n.readAt ? 'text-dark dark:text-white' : 'text-gray-600 dark:text-white-dark'}`}>{n.title}</p>
                                                                {n.body && <p className="text-xs text-gray-500 dark:text-white-dark/60 mt-0.5">{formatIsoDatesInTextBR(n.body)}</p>}
                                                                <p className="text-[10px] text-gray-400 dark:text-white-dark/40 mt-1">{relativeTime(n.createdAt)}</p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default NotificationsDrawer;
